/**
 * MapDealSuggestions — mirrored-deal results for the map search bar.
 *
 * Rendered by `MapSearchBar` into `MapboxSearch`'s results slot, above the
 * address results and under its own heading, like the hunting zones: a deal
 * and an address are different kinds of destination. Picking one reports the
 * deal summary — opening the card is a URL concern (`?deal=<id>`), and the
 * bar owns the camera move since only it knows a pick came from a search.
 *
 * The list is the deals payload the shell already polls, filtered in the
 * browser, so typing issues no request at all.
 */
import { useQuery } from '@tanstack/react-query';
import { CommandGroup, CommandItem } from '@/components/atoms/command';
import { HubSpotMark } from '@/features/deals/components/HubSpotMark';
import { dealsKeyFactory } from '@/features/locations/services/deals.api';
import type { IDealSummary } from '@/types/api';

/** Deal suggestions offered per search. Kept short so the address results below stay visible. */
const DEAL_SUGGESTION_LIMIT = 3;

/** One character matches most of the mirror, which is noise rather than a suggestion. */
const DEAL_QUERY_MIN_LENGTH = 2;

interface MapDealSuggestionsProps {
  /** The live search text, straight from the input. */
  query: string;
  /** Called with the picked deal. */
  onSelectDeal: (deal: IDealSummary) => void;
}

export function MapDealSuggestions({ query, onSelectDeal }: MapDealSuggestionsProps) {
  // Subscribes to the mirror the shell polls without adding a second poller.
  const { data } = useQuery({ ...dealsKeyFactory.list(), enabled: false });

  const needle = query.trim().toLowerCase();
  if (needle.length < DEAL_QUERY_MIN_LENGTH) {
    return null;
  }

  const deals = (data?.deals ?? [])
    .filter((deal) => deal.id === needle || deal.name.toLowerCase().includes(needle))
    .sort((a, b) => Number(b.id === needle) - Number(a.id === needle))
    .slice(0, DEAL_SUGGESTION_LIMIT);

  if (deals.length === 0) {
    return null;
  }

  return (
    <CommandGroup className="pb-1">
      <span className="text-sm text-primary-500">Deals</span>
      {deals.map((deal) => (
        <CommandItem
          key={deal.id}
          value={`deal-${deal.id}`}
          onSelect={() => onSelectDeal(deal)}
          className="cursor-pointer items-start hover:!bg-brand-150 data-[selected=true]:bg-brand-150"
        >
          <HubSpotMark className="mt-0.5 size-4 text-muted-foreground" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="whitespace-normal break-words text-sm font-medium">{deal.name}</span>
            <span className="text-xs opacity-70">{deal.stage}</span>
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}
